import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { SupabaseClient } from '@supabase/supabase-js'
import { useOmniAuth } from '@omni/auth'

// How up to date is the data (Sam, 16 Oct 2026): "Reports are only good if we
// have real data. We need to show the sync on the Command Centre, how up to
// date it is, because that is key to knowing whether Claude can actually
// query the data."
//
// One list of source definitions per Command Centre (the customer's
// surface-apps.<slug>.ts exports it as `freshnessSources`), read through the
// shell session, drawn twice: the full panel on the Data page and the pill in
// the top bar on every page (instance.freshnessPill). Pages show; nothing here
// starts a sync.

export type FreshnessTone = 'green' | 'amber' | 'red' | 'grey'

export interface FreshnessReading {
  /** When this source last updated (ISO), or null when it never has. */
  at: string | null
  /** One plain line under the row, e.g. "No new calls since 1 Sep". */
  detail?: string
}

export interface FreshnessSource {
  id: string
  /** Plain name, the owner's words: "Deals and stages". */
  name: string
  /** One short line: where it comes from or what is in it. */
  what: string
  /** How often it is meant to update, in words: "Every hour". */
  cadence: string
  /**
   * Minutes after which it counts as late. Green inside it, amber up to twice
   * it, red beyond (or never). Ignored for a source that is not connected.
   */
  windowMinutes?: number
  /** Counts toward the top-bar pill's colour. Everything else shows only in the list. */
  core?: boolean
  /** Not connected yet: drawn grey, never read, never turns anything red. */
  notConnected?: boolean
  /** Reads the newest time through the signed-in session. */
  read?: (db: SupabaseClient) => Promise<FreshnessReading>
}

export interface FreshnessRow {
  source: FreshnessSource
  /** undefined while loading; null when the read failed. */
  reading: FreshnessReading | null | undefined
  tone: FreshnessTone
  status: string
}

const NZ = 'Pacific/Auckland'

export const TONE_COLOUR: Record<FreshnessTone, string> = {
  green: '#1E8E3E',
  amber: '#C77700',
  red: '#D21F1F',
  grey: '#A3A3A0',
}

export function toneFor(at: string | null, windowMinutes: number, now: number): FreshnessTone {
  if (!at) return 'red'
  const age = (now - new Date(at).getTime()) / 60000
  if (!Number.isFinite(age)) return 'red'
  if (age <= windowMinutes) return 'green'
  if (age <= windowMinutes * 2) return 'amber'
  return 'red'
}

const STATUS: Record<FreshnessTone, string> = {
  green: 'Up to date',
  amber: 'Running late',
  red: 'Out of date',
  grey: 'Not connected yet',
}

/** "12 minutes ago", "3 hours ago", "6 days ago". */
export function agoLong(at: string, now: number): string {
  const min = Math.max(0, Math.round((now - new Date(at).getTime()) / 60000))
  if (min < 1) return 'just now'
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`
  const h = Math.round(min / 60)
  if (h < 48) return `${h} hour${h === 1 ? '' : 's'} ago`
  const d = Math.round(h / 24)
  return `${d} days ago`
}

/** "12m ago", "3h ago", "6d ago" for the pill. */
export function agoShort(at: string, now: number): string {
  const min = Math.max(0, Math.round((now - new Date(at).getTime()) / 60000))
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m ago`
  const h = Math.round(min / 60)
  if (h < 48) return `${h}h ago`
  return `${Math.round(h / 24)}d ago`
}

const dayKey = (t: number) => new Date(t).toLocaleDateString('en-CA', { timeZone: NZ })

/** NZ clock time: "1:07 pm today", "7:44 am yesterday", "1 Sep, 10:50 am". */
export function nzWhen(at: string, now: number = Date.now()): string {
  const t = new Date(at).getTime()
  const clock = new Date(t)
    .toLocaleTimeString('en-NZ', { timeZone: NZ, hour: 'numeric', minute: '2-digit' })
    .replace(/\s/g, ' ')
  if (dayKey(t) === dayKey(now)) return `${clock} today`
  if (dayKey(t) === dayKey(now - 86_400_000)) return `${clock} yesterday`
  const date = new Date(t).toLocaleDateString('en-NZ', { timeZone: NZ, day: 'numeric', month: 'short' })
  return `${date}, ${clock}`
}

/** NZ date only: "1 Sep". */
export function nzDate(at: string): string {
  return new Date(at).toLocaleDateString('en-NZ', { timeZone: NZ, day: 'numeric', month: 'short' })
}

/** A clock that ticks every 30 seconds so "12 minutes ago" stays true. */
export function useNow(): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(id)
  }, [])
  return now
}

/**
 * Reads every connected source, each settled on its own (one failed read never
 * hides the others), once a minute. The pill and the Data page share the key,
 * so they share one read.
 */
export function useFreshness(sources: FreshnessSource[]): { rows: FreshnessRow[]; loading: boolean; now: number } {
  const { getClient } = useOmniAuth()
  const now = useNow()
  const q = useQuery<Record<string, FreshnessReading | null>>({
    queryKey: ['data-freshness', sources.map((s) => s.id).join(',')],
    queryFn: async () => {
      const db = getClient('crm')
      const live = sources.filter((s) => !s.notConnected && s.read)
      const settled = await Promise.allSettled(live.map((s) => s.read!(db)))
      const out: Record<string, FreshnessReading | null> = {}
      live.forEach((s, i) => {
        const r = settled[i]
        out[s.id] = r?.status === 'fulfilled' ? r.value : null
      })
      return out
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
  })

  const rows = useMemo<FreshnessRow[]>(
    () =>
      sources.map((source) => {
        if (source.notConnected || !source.read) {
          return { source, reading: undefined, tone: 'grey', status: STATUS.grey }
        }
        const reading = q.data ? (q.data[source.id] ?? null) : undefined
        if (reading === undefined) return { source, reading, tone: 'grey', status: 'Checking' }
        if (reading === null) return { source, reading, tone: 'grey', status: 'Could not check just now' }
        const tone = toneFor(reading.at, source.windowMinutes ?? 60, now)
        return { source, reading, tone, status: reading.at ? STATUS[tone] : 'Never updated' }
      }),
    [sources, q.data, now],
  )

  return { rows, loading: q.isPending, now }
}

const RANK: Record<FreshnessTone, number> = { grey: 0, green: 1, amber: 2, red: 3 }

/**
 * The pill's verdict: the worst of the core sources that could be read. A
 * source outside the core (or not connected) never colours the pill.
 */
export function pillVerdict(rows: FreshnessRow[], loading: boolean, now: number): { tone: FreshnessTone; label: string } {
  const core = rows.filter((r) => r.source.core && r.reading && r.reading !== null)
  if (loading) return { tone: 'grey', label: 'Checking your data' }
  if (core.length === 0) return { tone: 'grey', label: 'Data check unavailable' }
  const worst = core.reduce((a, b) => (RANK[b.tone] > RANK[a.tone] ? b : a))
  if (worst.tone === 'green') {
    // Green: how fresh the main copy is (the first core source).
    const at = core[0].reading!.at!
    return { tone: 'green', label: `Data up to date · ${agoShort(at, now)}` }
  }
  const at = worst.reading!.at
  const word = worst.tone === 'amber' ? 'Data running late' : 'Data out of date'
  return { tone: worst.tone, label: at ? `${word} · ${agoShort(at, now)}` : `${word} · ${worst.source.name}` }
}
